import { useState, useRef } from "react";
import { Checkbox } from "@mantine/core";
import { useQuery } from "@apollo/client/react";
import { gql } from "@/__generated__";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpFromBracket } from "@fortawesome/sharp-regular-svg-icons";
import styles from "./patient-consent-section.module.scss";

const GET_PROVIDER = gql(`
  query GetProvider {
    provider {
      providerId
      hasSignedBAA
      providerName
    }
  }
`);

interface PatientConsentSectionProps {
  appealId: string;
  hasPatientConsent?: boolean;
  onConsentChange: (hasConsent: boolean, document?: File | null) => void;
  disabled?: boolean;
  hasError?: boolean;
  existingDocument?: {
    fileName: string;
    url: string;
  } | null;
}

export function PatientConsentSection({
  hasPatientConsent = false,
  onConsentChange,
  disabled = false,
  hasError = false,
  existingDocument = null,
}: PatientConsentSectionProps) {
  const { data, loading } = useQuery(GET_PROVIDER, { fetchPolicy: "no-cache" });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  if (loading) {
    return null;
  }

  if (data?.provider?.hasSignedBAA) {
    return null;
  }

  const handleConsentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onConsentChange(event.currentTarget.checked);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const acceptedTypes = [
      "application/pdf",
      "image/png",
      "image/jpeg",
      "image/jpg",
    ];
    if (!acceptedTypes.includes(file.type)) {
      setUploadError(
        "File type not supported. Please upload a PDF, PNG, or JPG file.",
      );
      return;
    }

    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError("File is too large. Maximum size is 20MB.");
      return;
    }

    setUploadError(null);
    setUploadedFile(file);

    onConsentChange(hasPatientConsent, file);
  };

  const organizationName = data?.provider?.providerName || "Your organization";

  return (
    <div
      className={`${styles.container} ${hasError ? styles.containerError : ""}`}
      data-consent-section
    >
      <div className={styles.header}>
        <h3 className={styles.title}>Patient consent</h3>
        <p className={styles.description}>
          Please confirm that you have obtained the necessary consent from your
          patient to share their information for this appeal.
        </p>
      </div>

      <div className={styles.checkboxWrapper}>
        <Checkbox
          required
          label={`${organizationName} has consent from patient to share data with Claimable`}
          checked={hasPatientConsent}
          onChange={handleConsentChange}
          disabled={disabled}
          error={hasError}
          data-testid="patient-consent-checkbox"
          className={styles.checkbox}
          classNames={{
            inner: styles.checkboxContainer,
            input: hasError ? styles.checkboxInputError : styles.checkboxInput,
            icon: styles.checkboxIcon,
            label: hasError ? styles.checkboxLabelError : "",
          }}
        />
        {hasError && (
          <p className={styles.errorMessage}>
            This field is required. Please check the box to confirm patient
            consent.
          </p>
        )}
      </div>

      <div className={styles.uploadSection}>
        <button
          className={styles.uploadContent}
          type="button"
          onClick={handleUploadClick}
          disabled={disabled}
          aria-label="Upload consent document"
        >
          <div className={styles.uploadTextContent}>
            <p className={styles.uploadTitle}>
              {uploadedFile
                ? `Uploaded: ${uploadedFile.name}`
                : existingDocument
                  ? `Document on file: ${existingDocument.fileName}`
                  : "Add supporting consent document (optional)"}
            </p>
            {!uploadedFile && !existingDocument && (
              <p className={styles.uploadDescription}>
                Add a copy of signed consent document. This is for optional
                record keeping and is not attached to the referral or appeal.
              </p>
            )}
            {uploadedFile && (
              <p className={styles.uploadDescription}>
                File ready to upload. Size:{" "}
                {(uploadedFile.size / 1024).toFixed(1)} KB
              </p>
            )}
            {!uploadedFile && existingDocument && (
              <p className={styles.uploadDescription}>
                A consent document has been uploaded. You can upload a new one
                to replace it.
              </p>
            )}
            {uploadError && <p className={styles.uploadError}>{uploadError}</p>}
          </div>
          <div className={styles.uploadIcon}>
            <FontAwesomeIcon icon={faArrowUpFromBracket} />
          </div>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
      </div>
    </div>
  );
}
